import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Collapse } from 'react-bootstrap';
import api from './utils/api'; // 使用配置好的 axios instance

const CheckIn = () => {
  const [mobile, setMobile] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: 選擇方式, 2: 選擇社別
  const [activityInfo, setActivityInfo] = useState(null);
  const [checkInMethod, setCheckInMethod] = useState(null); // 'line', 'mobile', 'face'
  const [checkInStats, setCheckInStats] = useState(null); // 簽到統計
  const [showOtherMethods, setShowOtherMethods] = useState(false); // 展開其他簽到方式

  // Face recognition states
  const [cameraStream, setCameraStream] = useState(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [faceAvailable, setFaceAvailable] = useState(false);
  const [recognizedEmployee, setRecognizedEmployee] = useState(null);
  const [autoCapturing, setAutoCapturing] = useState(false); // 自動拍照中
  const [countdown, setCountdown] = useState(0); // 倒數計時

  // Role selection states (多帳號選擇)
  const [roleSelectionMode, setRoleSelectionMode] = useState(false);
  const [pendingRoles, setPendingRoles] = useState([]);
  const [roleSelectionToken, setRoleSelectionToken] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const captureRef = useRef(null); // 保存 captureAndRecognize 函數的引用

  const location = useLocation();
  const navigate = useNavigate();

  // 從 URL 獲取參數
  const searchParams = new URLSearchParams(location.search);
  const activityId = searchParams.get('aid');
  const checkInType = searchParams.get('type') || '10';
  const selectRoleToken = searchParams.get('selectRole');

  useEffect(() => {
    if (!activityId) {
      setError('無效的活動ID');
      return;
    }

    // 檢查是否有成功或錯誤訊息
    const successMsg = searchParams.get('success');
    const errorMsg = searchParams.get('error');

    if (successMsg) {
      setSuccess(decodeURIComponent(successMsg));
    } else if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
    }

    // 檢查是否需要多帳號選擇
    if (selectRoleToken) {
      setRoleSelectionToken(selectRoleToken);
      setRoleSelectionMode(true);
      fetchPendingRoles(selectRoleToken);
    }

    // 獲取活動資訊
    fetchActivityInfo();
    // 檢查人臉識別服務可用性
    checkFaceAvailability();
  }, [activityId, selectRoleToken]);

  // 組件卸載時清理相機資源
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // 當 cameraStream 準備好時設置 video srcObject
  useEffect(() => {
    if (cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  // 頁面離開時關閉相機
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
        setHasCamera(false);
        setAutoCapturing(false);
        setCountdown(0);
        // 如果正在人臉識別模式，返回選擇方式
        if (checkInMethod === 'face') {
          setCheckInMethod(null);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cameraStream, checkInMethod]);

  // 相機就緒後自動開始倒數並拍照
  useEffect(() => {
    if (hasCamera && checkInMethod === 'face' && !isCapturing && !autoCapturing && !success && !error) {
      setAutoCapturing(true);
      setCountdown(3);
    }
  }, [hasCamera, checkInMethod, isCapturing, autoCapturing, success, error]);

  // 倒數計時並自動拍照
  useEffect(() => {
    if (countdown > 0 && autoCapturing) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && autoCapturing && hasCamera && captureRef.current) {
      setAutoCapturing(false);
      captureRef.current();
    }
  }, [countdown, autoCapturing, hasCamera]);

  // 獲取活動資訊
  const fetchActivityInfo = async () => {
    try {
      const response = await api.get(`/activities/${activityId}/public`);
      if (response.data) {
        setActivityInfo({
          id: activityId,
          name: response.data.a_name || '活動',
          startDate: response.data.a_Sdate,
          endDate: response.data.a_Edate,
          location: response.data.a_location,
          status: response.data.a_status,
          storeName: response.data.store_name
        });
      }
      // 同時獲取簽到統計
      fetchCheckInStats();
    } catch (error) {
      console.error('Failed to fetch activity info:', error);
      setActivityInfo({
        id: activityId,
        name: '活動',
      });
    }
  };

  // 獲取簽到統計
  const fetchCheckInStats = async () => {
    try {
      const response = await api.get(`/checkin/stats/${activityId}`);
      if (response.data?.success) {
        setCheckInStats(response.data.data);
      }
    } catch (error) {
      console.log('Failed to fetch check-in stats:', error.message);
    }
  };

  // 格式化日期時間
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  };

  // 檢查活動是否進行中
  const isActivityActive = () => {
    if (!activityInfo?.startDate) return true; // 沒有日期資訊時預設進行中
    const now = new Date();
    const start = new Date(activityInfo.startDate);
    const end = activityInfo.endDate ? new Date(activityInfo.endDate) : null;

    if (now < start) return false; // 尚未開始
    if (end && now > end) return false; // 已結束
    return true;
  };

  // 檢查人臉識別服務可用性
  const checkFaceAvailability = async () => {
    try {
      const response = await api.get('/checkin/face/availability');
      if (response.data?.success && response.data?.data?.available) {
        setFaceAvailable(true);
      }
    } catch (error) {
      console.log('Face recognition not available:', error.message);
      setFaceAvailable(false);
    }
  };

  // 獲取待選擇的帳號列表
  const fetchPendingRoles = async (token) => {
    try {
      setLoading(true);
      const response = await api.get(`/checkin/line/pending-roles?token=${token}`);
      if (response.data?.success) {
        setPendingRoles(response.data.data.roles);
      } else {
        setError(response.data?.error || '無法獲取帳號列表');
        setRoleSelectionMode(false);
      }
    } catch (error) {
      console.error('Fetch pending roles error:', error);
      setError(error.response?.data?.error || '無法獲取帳號列表，請重新使用 LINE 簽到');
      setRoleSelectionMode(false);
    } finally {
      setLoading(false);
    }
  };

  // 選擇帳號並完成簽到
  const handleRoleSelect = async (employeeId) => {
    try {
      setSelectedRoleId(employeeId);
      setLoading(true);
      setError('');

      const response = await api.post('/checkin/line/select-role', {
        token: roleSelectionToken,
        employeeId: employeeId
      });

      if (response.data?.success) {
        const emp = response.data.data.employee;
        setRecognizedEmployee(emp);
        setSuccess(response.data.data.message);
        setRoleSelectionMode(false);

        // 3秒後關閉頁面
        setTimeout(() => {
          window.close() || navigate('/');
        }, 3000);
      } else {
        setError(response.data?.error || '簽到失敗');
        setSelectedRoleId(null);
      }
    } catch (error) {
      console.error('Role select error:', error);
      setError(error.response?.data?.error || '簽到處理失敗');
      setSelectedRoleId(null);
    } finally {
      setLoading(false);
    }
  };

  // 取消多帳號選擇，返回首頁
  const cancelRoleSelection = () => {
    setRoleSelectionMode(false);
    setRoleSelectionToken(null);
    setPendingRoles([]);
    setCheckInMethod(null);
    setMobile('');
    // 移除 URL 中的 selectRole 參數
    navigate(`/checkin/mobile?aid=${activityId}`, { replace: true });
  };

  // 手機號碼多身份選擇後簽到
  const handleMobileRoleSelect = async (employeeId) => {
    try {
      setSelectedRoleId(employeeId);
      setLoading(true);
      setError('');

      // 找到選擇的角色取得 storeId
      const selectedRole = pendingRoles.find(r => r.e_id === employeeId);
      if (!selectedRole) {
        setError('無效的身份選擇');
        setSelectedRoleId(null);
        return;
      }

      const response = await api.post('/checkin/record', {
        mobile: mobile.trim(),
        storeId: selectedRole.e_ssid,
        activityId,
        type: checkInType
      });

      if (response.data.success) {
        setRecognizedEmployee({
          name: selectedRole.e_name,
          store: selectedRole.store_name
        });
        setSuccess(`簽到成功！歡迎 ${selectedRole.e_name}`);
        setRoleSelectionMode(false);

        // 3秒後關閉頁面
        setTimeout(() => {
          window.close() || navigate('/');
        }, 3000);
      }
    } catch (error) {
      console.error('Mobile role select error:', error);
      if (error.response?.status === 409 || error.response?.data?.code === 'DUPLICATE_CHECKIN') {
        const record = error.response?.data?.existingRecord;
        const name = record?.employeeName || pendingRoles.find(r => r.e_id === employeeId)?.e_name || '您';
        const timeStr = record?.formattedTime ? `\n簽到時間：${record.formattedTime}` : '';
        setError(`${name} 已經簽到過了${timeStr}`);
      } else {
        setError(error.response?.data?.error || '簽到失敗');
      }
      setSelectedRoleId(null);
    } finally {
      setLoading(false);
    }
  };

  // 初始化相機
  const initializeCamera = useCallback(async () => {
    try {
      setError('');

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('瀏覽器不支援相機');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          facingMode: 'user'
        },
        audio: false
      });

      setCameraStream(stream);
      setHasCamera(true);

    } catch (err) {
      let errorMessage = '相機啟動失敗: ';
      if (err.name === 'NotAllowedError') {
        errorMessage += '相機權限被拒絕，請允許使用相機';
      } else if (err.name === 'NotFoundError') {
        errorMessage += '找不到相機設備';
      } else if (err.name === 'NotReadableError') {
        errorMessage += '相機被其他應用程式使用';
      } else {
        errorMessage += err.message;
      }
      setError(errorMessage);
      setHasCamera(false);
    }
  }, []);

  // 停止相機
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setHasCamera(false);
  }, [cameraStream]);

  // 拍照並進行人臉識別
  const captureAndRecognize = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    setLoading(true);
    setError('');

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // 設定 canvas 尺寸
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      // 鏡像翻轉繪製
      context.save();
      context.scale(-1, 1);
      context.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
      context.restore();

      // 轉換為 base64
      const photoData = canvas.toDataURL('image/jpeg', 0.9);

      // 呼叫人臉識別 API
      const response = await api.post('/checkin/face/verify', {
        photo: photoData,
        activityId: activityId
      });

      if (response.data.success) {
        setRecognizedEmployee(response.data.data.employee);
        setSuccess(`簽到成功！歡迎 ${response.data.data.employee.name}`);
        stopCamera();

        // 3秒後關閉頁面
        setTimeout(() => {
          window.close() || navigate('/');
        }, 3000);
      } else if (response.data.code === 'DUPLICATE_CHECKIN') {
        // 重複簽到 - 顯示姓名和時間
        const record = response.data.existingRecord;
        const name = record?.employeeName || response.data.employee?.name || '您';
        const timeStr = record?.formattedTime ? `\n簽到時間：${record.formattedTime}` : '';
        setError(`${name} 已經簽到過了${timeStr}`);
        stopCamera();
      } else {
        throw new Error(response.data.error || '人臉識別失敗');
      }

    } catch (err) {
      console.error('Face recognition error:', err);
      if (err.response?.data?.code === 'DUPLICATE_CHECKIN') {
        const record = err.response?.data?.existingRecord;
        const name = record?.employeeName || '您';
        const timeStr = record?.formattedTime ? `\n簽到時間：${record.formattedTime}` : '';
        setError(`${name} 已經簽到過了${timeStr}`);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(err.message || '人臉識別失敗，請重試');
      }
    } finally {
      setIsCapturing(false);
      setLoading(false);
    }
  }, [activityId, stopCamera, navigate]);

  // 保存 captureAndRecognize 函數的引用，供 useEffect 使用
  useEffect(() => {
    captureRef.current = captureAndRecognize;
  }, [captureAndRecognize]);

  // 選擇簽到方式
  const selectCheckInMethod = (method) => {
    setCheckInMethod(method);
    setError('');
    if (method === 'face') {
      initializeCamera();
    }
  };

  // 返回選擇簽到方式
  const backToMethodSelection = () => {
    setCheckInMethod(null);
    setStep(1);
    setMobile('');
    setSelectedStore('');
    setStores([]);
    setError('');
    setAutoCapturing(false);
    setCountdown(0);
    stopCamera();
  };

  // 處理手機號碼提交
  const handleMobileSubmit = async (e) => {
    e.preventDefault();

    if (!mobile.trim()) {
      setError('請輸入您的手機號碼');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/checkin/verify-mobile', {
        mobile: mobile.trim(),
        activityId,
        type: checkInType
      });

      if (response.data.success) {
        if (response.data.needStoreSelection && response.data.roles) {
          // 多個身份，使用角色選擇 UI
          setPendingRoles(response.data.roles);
          setStores(response.data.stores);
          setRoleSelectionMode(true);
          setCheckInMethod('mobile-select'); // 標記為手機多身份選擇
        } else {
          // 只有一個身份，直接簽到
          await handleDirectCheckIn(response.data.storeId);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setError('很抱歉，您輸入的手機號碼不存在，請重新輸入！');
      } else {
        setError('驗證失敗，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  };

  // 處理社別選擇提交
  const handleStoreSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStore) {
      setError('請選擇社別');
      return;
    }

    await handleDirectCheckIn(selectedStore);
  };

  // 直接簽到處理
  const handleDirectCheckIn = async (storeId) => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/checkin/record', {
        mobile: mobile.trim(),
        storeId,
        activityId,
        type: checkInType
      });

      if (response.data.success) {
        setSuccess('簽到成功！');
        
        // 3秒後關閉頁面或導向成功頁面
        setTimeout(() => {
          window.close() || navigate('/');
        }, 3000);
      }
    } catch (error) {
      if (error.response?.status === 409 || error.response?.data?.code === 'DUPLICATE_CHECKIN') {
        const record = error.response?.data?.existingRecord;
        const name = record?.employeeName || '您';
        const timeStr = record?.formattedTime ? `\n簽到時間：${record.formattedTime}` : '';
        setError(`${name} 已經簽到過了${timeStr}`);
      } else {
        setError('簽到失敗，請稍後再試');
      }
    } finally {
      setLoading(false);
    }
  };

  // 返回重新輸入手機號碼
  const handleBackToMobile = () => {
    setStep(1);
    setSelectedStore('');
    setStores([]);
    setError('');
  };

  // 處理 LINE 登入
  const handleLineLogin = () => {
    const lineAuthUrl = `/api/checkin/line/auth?activityId=${activityId}`;
    window.location.href = lineAuthUrl;
  };

  if (success) {
    return (
      <Container className="py-4">
        <Card style={{
          maxWidth: '420px',
          margin: '0 auto',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <Card.Body className="text-center" style={{ padding: '40px 24px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'rgba(40, 167, 69, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <span style={{ fontSize: '40px' }}>✅</span>
            </div>
            <h4 style={{
              color: '#28a745',
              marginBottom: '16px',
              fontWeight: '600',
              fontSize: '20px'
            }}>
              {success}
            </h4>
            {recognizedEmployee && (
              <p style={{
                color: '#495057',
                marginBottom: '16px',
                fontSize: '16px',
                fontWeight: '500'
              }}>
                {recognizedEmployee.store} - {recognizedEmployee.name}
              </p>
            )}
            {activityInfo && (
              <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '8px' }}>
                {activityInfo.name}
              </p>
            )}
            <p className="text-muted" style={{ fontSize: '13px' }}>
              頁面將在 3 秒後自動關閉
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card style={{ maxWidth: '420px', margin: '0 auto', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Card.Header className="text-center" style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px 16px 0 0',
          padding: '24px 20px'
        }}>
          <h4 className="mb-2 text-white" style={{ fontWeight: '700', fontSize: '22px' }}>
            📝 活動簽到
          </h4>
          {activityInfo && (
            <>
              <h5 className="mb-2 text-white" style={{ fontWeight: '600', fontSize: '18px', opacity: 0.95 }}>
                {activityInfo.name}
              </h5>
              {activityInfo.startDate && (
                <div className="text-white" style={{ fontSize: '14px', opacity: 0.85 }}>
                  <span>🗓️ {formatDateTime(activityInfo.startDate)}</span>
                  {activityInfo.location && (
                    <span className="ms-2">📍 {activityInfo.location}</span>
                  )}
                </div>
              )}
              <div className="mt-2">
                {isActivityActive() ? (
                  <span className="badge" style={{
                    backgroundColor: 'rgba(40, 167, 69, 0.9)',
                    fontSize: '12px',
                    padding: '5px 12px',
                    borderRadius: '12px'
                  }}>
                    🟢 活動進行中
                  </span>
                ) : (
                  <span className="badge" style={{
                    backgroundColor: 'rgba(108, 117, 125, 0.9)',
                    fontSize: '12px',
                    padding: '5px 12px',
                    borderRadius: '12px'
                  }}>
                    ⚪ 活動已結束
                  </span>
                )}
                {checkInStats && (
                  <span className="badge ms-2" style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    fontSize: '12px',
                    padding: '5px 12px',
                    borderRadius: '12px'
                  }}>
                    已有 {checkInStats.total || 0} 人簽到
                  </span>
                )}
              </div>
            </>
          )}
        </Card.Header>
        <Card.Body style={{ padding: '24px' }}>
          {error && (
            <Alert variant="danger" className="mb-3">
              <div style={{ whiteSpace: 'pre-line' }}>
                {error}
              </div>
            </Alert>
          )}

          {/* 多帳號選擇模式 (LINE 或 手機) */}
          {roleSelectionMode && pendingRoles.length > 0 && (
            <>
              <div className="text-center mb-4">
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(13, 110, 253, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <span style={{ fontSize: '28px' }}>👤</span>
                </div>
                <h6 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>
                  偵測到多個帳號
                </h6>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  請選擇要簽到的身份
                </p>
              </div>

              {pendingRoles.map((role) => (
                <Card
                  key={role.e_id}
                  className="mb-3"
                  style={{
                    cursor: selectedRoleId ? 'not-allowed' : 'pointer',
                    opacity: selectedRoleId && selectedRoleId !== role.e_id ? 0.5 : 1,
                    border: selectedRoleId === role.e_id ? '2px solid #0d6efd' : '1px solid #e9ecef',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedRoleId === role.e_id ? '0 4px 12px rgba(13, 110, 253, 0.15)' : 'none'
                  }}
                  onClick={() => !selectedRoleId && (
                    checkInMethod === 'mobile-select'
                      ? handleMobileRoleSelect(role.e_id)
                      : handleRoleSelect(role.e_id)
                  )}
                >
                  <Card.Body className="py-3 px-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '16px', color: '#2c3e50' }}>
                          {role.store_name}
                        </div>
                        <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '2px' }}>
                          {role.e_name} · {role.position_name}
                        </div>
                      </div>
                      <div>
                        {selectedRoleId === role.e_id ? (
                          <Spinner animation="border" size="sm" variant="primary" />
                        ) : (
                          <span style={{
                            fontSize: '18px',
                            color: '#0d6efd',
                            fontWeight: 'bold'
                          }}>→</span>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}

              <Button
                variant="outline-secondary"
                className="w-100 mt-2"
                style={{ borderRadius: '10px', padding: '12px' }}
                onClick={cancelRoleSelection}
                disabled={selectedRoleId !== null}
              >
                取消，重新選擇簽到方式
              </Button>
            </>
          )}

          {step === 1 && !checkInMethod && !roleSelectionMode && (
            <>
              {/* 主要簽到區塊 */}
              <div className="text-center" style={{ padding: '8px 0' }}>
                {/* LINE 主要 CTA 按鈕 */}
                <Button
                  variant="success"
                  className="w-100"
                  style={{
                    fontSize: '18px',
                    padding: '16px 24px',
                    fontWeight: '600',
                    backgroundColor: '#00B900',
                    borderColor: '#00B900',
                    borderRadius: '12px',
                    height: '56px',
                    boxShadow: '0 4px 12px rgba(0, 185, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={handleLineLogin}
                  disabled={loading}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                  </svg>
                  LINE 快速簽到
                </Button>

                <p className="text-muted mt-2 mb-4" style={{ fontSize: '13px' }}>
                  推薦使用 LINE 快速完成簽到
                </p>

                {/* 其他方式展開區塊 */}
                <div className="mt-3">
                  <Button
                    variant="link"
                    className="text-muted p-0"
                    style={{ fontSize: '14px', textDecoration: 'none' }}
                    onClick={() => setShowOtherMethods(!showOtherMethods)}
                  >
                    {showOtherMethods ? '▲ 收起其他方式' : '▼ 其他簽到方式'}
                  </Button>

                  <Collapse in={showOtherMethods}>
                    <div className="mt-3">
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          className="flex-fill"
                          style={{
                            fontSize: '14px',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            borderColor: '#dee2e6'
                          }}
                          onClick={() => selectCheckInMethod('mobile')}
                          disabled={loading}
                        >
                          <span style={{ marginRight: '6px' }}>📞</span>
                          手機號碼
                        </Button>

                        {faceAvailable && (
                          <Button
                            variant="outline-secondary"
                            className="flex-fill"
                            style={{
                              fontSize: '14px',
                              padding: '12px 16px',
                              borderRadius: '10px',
                              borderColor: '#dee2e6'
                            }}
                            onClick={() => selectCheckInMethod('face')}
                            disabled={loading}
                          >
                            <span style={{ marginRight: '6px' }}>😊</span>
                            人臉識別
                          </Button>
                        )}
                      </div>
                    </div>
                  </Collapse>
                </div>
              </div>
            </>
          )}

          {/* 手機號碼簽到 */}
          {step === 1 && checkInMethod === 'mobile' && !roleSelectionMode && (
            <>
              <div className="text-center mb-4">
                <Button
                  variant="link"
                  size="sm"
                  onClick={backToMethodSelection}
                  className="text-muted mb-3"
                  style={{ padding: 0, textDecoration: 'none', fontSize: '14px' }}
                >
                  ← 返回選擇簽到方式
                </Button>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(13, 110, 253, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <span style={{ fontSize: '28px' }}>📞</span>
                </div>
                <h6 style={{
                  color: '#2c3e50',
                  fontSize: '18px',
                  fontWeight: '600'
                }}>
                  手機號碼簽到
                </h6>
              </div>

              <Form onSubmit={handleMobileSubmit}>
                <Form.Group className="mb-4">
                  <Form.Control
                    type="tel"
                    placeholder="請輸入您的手機號碼"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    style={{
                      fontSize: '18px',
                      padding: '16px',
                      textAlign: 'center',
                      border: '2px solid #e9ecef',
                      borderRadius: '12px'
                    }}
                    disabled={loading}
                    autoFocus
                  />
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  className="w-100"
                  style={{
                    fontSize: '16px',
                    padding: '14px',
                    fontWeight: '600',
                    borderRadius: '12px'
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      驗證中...
                    </>
                  ) : (
                    '驗證並簽到'
                  )}
                </Button>
              </Form>
            </>
          )}

          {/* 人臉識別簽到 */}
          {step === 1 && checkInMethod === 'face' && !roleSelectionMode && (
            <>
              <div className="text-center mb-3">
                <Button
                  variant="link"
                  size="sm"
                  onClick={backToMethodSelection}
                  className="text-muted mb-3"
                  style={{ padding: 0, textDecoration: 'none', fontSize: '14px' }}
                >
                  ← 返回選擇簽到方式
                </Button>
                <h6 style={{
                  color: '#2c3e50',
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}>
                  <span style={{ marginRight: '8px' }}>😊</span>
                  人臉識別簽到
                </h6>
              </div>

              {/* 相機預覽 */}
              <div className="text-center mb-3">
                <div style={{
                  position: 'relative',
                  display: 'inline-block',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      maxHeight: '225px',
                      objectFit: 'cover',
                      transform: 'scaleX(-1)',
                      backgroundColor: '#1a1a2e',
                      display: 'block'
                    }}
                  />
                  {/* 倒數計時覆蓋層 */}
                  {countdown > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#fff',
                      fontSize: '72px',
                      fontWeight: 'bold',
                      textShadow: '0 2px 10px rgba(0,0,0,0.5)'
                    }}>
                      {countdown}
                    </div>
                  )}
                  {/* 識別中覆蓋層 */}
                  {(loading || isCapturing) && countdown === 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#fff',
                      textAlign: 'center'
                    }}>
                      <Spinner animation="border" />
                      <p className="mb-0 mt-2" style={{ fontSize: '14px' }}>識別中...</p>
                    </div>
                  )}
                  {!hasCamera && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#fff'
                    }}>
                      <Spinner animation="border" size="sm" />
                      <p className="mb-0 mt-2" style={{ fontSize: '12px' }}>啟動相機中...</p>
                    </div>
                  )}
                </div>
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>

              <p className="text-center text-muted mb-4" style={{ fontSize: '13px' }}>
                {!hasCamera
                  ? '正在啟動相機...'
                  : countdown > 0
                    ? '請將臉部置於框內，即將自動拍照'
                    : (loading || isCapturing)
                      ? '正在識別您的身份...'
                      : '請將臉部置於框內'}
              </p>

              {/* 只有在錯誤時才顯示重試按鈕 */}
              {error && (
                <Button
                  variant="info"
                  className="w-100"
                  style={{
                    fontSize: '16px',
                    padding: '14px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    backgroundColor: '#17a2b8',
                    borderColor: '#17a2b8'
                  }}
                  onClick={() => {
                    setError('');
                    setAutoCapturing(true);
                    setCountdown(3);
                  }}
                  disabled={loading || isCapturing || !hasCamera}
                >
                  <span style={{ marginRight: '8px' }}>🔄</span>
                  重新識別
                </Button>
              )}
            </>
          )}

          {step === 2 && (
            <Form onSubmit={handleStoreSubmit}>
              <div className="text-center mb-4">
                <h2 style={{
                  color: '#2c3e50',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  選擇社別
                </h2>
                <p className="text-muted">手機號碼：{mobile}</p>
              </div>

              <Form.Group className="mb-3">
                <Form.Select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  style={{
                    fontSize: '16px',
                    padding: '12px',
                    borderRadius: '10px'
                  }}
                  disabled={loading}
                >
                  <option value="">請選擇社別</option>
                  {stores.map((store) => (
                    <option key={store.ss_id} value={store.ss_id}>
                      {store.ss_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div className="d-grid gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  style={{
                    fontSize: '16px',
                    padding: '14px',
                    fontWeight: 'bold',
                    borderRadius: '10px'
                  }}
                  disabled={loading || !selectedStore}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      簽到中...
                    </>
                  ) : (
                    '確認簽到'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline-secondary"
                  style={{ borderRadius: '10px' }}
                  onClick={backToMethodSelection}
                  disabled={loading}
                >
                  返回選擇簽到方式
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CheckIn;